import { Controller } from "@hotwired/stimulus"
import { showToast } from "./toast"
import { showAiOverlay, hideAiOverlay } from "./ai_overlay"

export default class extends Controller {
  static targets = ["button", "buttonText", "spinner"]
  static values = {
    url: String,
    identifyUrl: String,
    searchImagesUrl: String,
    uploadImageUrl: String,
    authorId: Number,
    name: String
  }

  get disambiguationController() {
    const el = document.getElementById("ai-disambiguation-dialog")
    if (!el) return null
    return this.application.getControllerForElementAndIdentifier(el, "ai-disambiguation")
  }

  get photoPickerController() {
    const el = document.getElementById("photo-picker-dialog")
    if (!el) return null
    return this.application.getControllerForElementAndIdentifier(el, "photo-picker")
  }

  async generate(event) {
    event.preventDefault()

    if (!this.urlValue) {
      showToast("AI auto-generate not configured")
      return
    }

    this.setLoading(true)

    try {
      let photoName = this.nameValue || ""

      if (this.hasIdentifyUrlValue && this.identifyUrlValue && this.hasNameValue && this.nameValue) {
        const identified = await this.identifyAuthor(this.nameValue)

        if (identified === null) {
          await this.directGenerate()
          await this.searchAndPickPhoto(photoName)
          return
        }

        if (identified === "cancelled") return

        if (identified.skip_disambiguation) {
          const canonicalName = identified.data.canonical_name || this.nameValue
          photoName = canonicalName
          await this.directGenerate(canonicalName)
          await this.searchAndPickPhoto(photoName)
          return
        }

        const disambiguation = this.disambiguationController
        if (!disambiguation) {
          await this.directGenerate()
          await this.searchAndPickPhoto(photoName)
          return
        }

        const selectedIndex = await disambiguation.show(identified.candidates, "author")
        if (!this.element.isConnected) return

        if (selectedIndex === null) return

        const selected = identified.candidates[selectedIndex]
        photoName = selected.name || this.nameValue
        await this.directGenerate(photoName)
        await this.searchAndPickPhoto(photoName)
      } else {
        await this.directGenerate()
        await this.searchAndPickPhoto(photoName)
      }
    } catch (error) {
      console.error("Author auto-generate error:", error)
      showToast("An error occurred while generating data")
    } finally {
      this.setLoading(false)
    }
  }

  async identifyAuthor(name) {
    try {
      const response = await fetch(this.identifyUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({ name })
      })

      if (response.status === 402) {
        const data = await response.json()
        if (data.redirect_url) window.open(data.redirect_url, "_blank")
        return "cancelled"
      }

      if (!response.ok) return null

      const data = await response.json()

      if (!data.skip_disambiguation && (!data.candidates || data.candidates.length === 0)) {
        showToast("No matching author found")
        return "cancelled"
      }

      return data
    } catch (error) {
      console.error("Identify error:", error)
      return null
    }
  }

  async directGenerate(canonicalName = null) {
    const body = canonicalName ? { canonical_name: canonicalName } : {}

    const response = await fetch(this.urlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
      },
      body: JSON.stringify(body)
    })

    const data = await response.json()

    if (response.ok) {
      this._generateSucceeded = data.updated || false
      if (!data.updated) {
        showToast("No new data was found", "notice")
      }
    } else {
      this._generateSucceeded = false
      showToast(data.error || "Failed to auto-generate")
    }
  }

  async searchAndPickPhoto(name) {
    if (!this.hasSearchImagesUrlValue || !this.searchImagesUrlValue) {
      if (this._generateSucceeded) window.location.reload()
      return
    }

    try {
      const response = await fetch(this.searchImagesUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({ name })
      })

      if (!response.ok) {
        if (this._generateSucceeded) window.location.reload()
        return
      }

      const data = await response.json()
      const images = data.images || []

      if (images.length === 0) {
        showToast("No photos found", "notice")
        if (this._generateSucceeded) window.location.reload()
        return
      }

      const picker = this.photoPickerController
      if (!picker) {
        if (this._generateSucceeded) window.location.reload()
        return
      }

      const selectedIndex = await picker.show(images, name)
      if (!this.element.isConnected) return

      if (selectedIndex === null) {
        if (this._generateSucceeded) window.location.reload()
        return
      }

      const selected = images[selectedIndex]
      let finalUrl = selected.url

      if (this.hasUploadImageUrlValue && this.uploadImageUrlValue) {
        const uploadResp = await fetch(this.uploadImageUrlValue, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
          },
          body: JSON.stringify({ url: selected.url })
        })

        if (uploadResp.ok) {
          const uploadData = await uploadResp.json()
          finalUrl = uploadData.url || finalUrl
        }
      }

      if (this.hasAuthorIdValue && this.authorIdValue) {
        await fetch(`/app/authors/${this.authorIdValue}`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
          },
          body: JSON.stringify({ author: { photo_url: finalUrl } })
        })
      }

      window.location.reload()
    } catch (error) {
      console.error("Photo search error:", error)
      if (this._generateSucceeded) window.location.reload()
    }
  }

  setLoading(isLoading) {
    if (isLoading) {
      showAiOverlay("AI Generating...")
    } else {
      hideAiOverlay()
    }

    if (this.hasButtonTarget) {
      this.buttonTarget.disabled = isLoading
    }

    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.toggle("hidden", !isLoading)
    }

    if (this.hasButtonTextTarget) {
      this.buttonTextTarget.textContent = isLoading ? "Generating..." : "AI Auto Generate"
    }
  }
}
